'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Ellipsis, Truck } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ShippingRateItem } from '@/lib/api/shippingRates'
import { ShippingRateResourceBadge } from '@/components/shippingRate/ShippingRateRespirceBadge'

interface GetShippingRatesTableColumnsProps {
	storeId: string
	onEditShippingRate: (shippingRateId: string) => void
	onDeleteShippingRate: (shippingRateId: string) => void
}

export function useShippingRatesTableColumns({
	storeId,
	onEditShippingRate,
	onDeleteShippingRate,
}: GetShippingRatesTableColumnsProps): ColumnDef<ShippingRateItem>[] {
	const { t } = useTranslation('shippings')

	return React.useMemo(
		() => [
			{
				id: 'select',
				header: ({ table }) => (
					<Checkbox
						checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label={t('table.selectAll')}
						className="translate-y-0.5"
					/>
				),
				cell: ({ row }) => (
					<DataTableCheckbox row={row} />
				),
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
			{
				id: 'id',
				accessorKey: 'id',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.id')} />,
				cell: ({ row }) => {
					const shippingRate = row.original
					return (
						<ShippingRateResourceBadge
							shippingRateId={shippingRate.id}
							variant="secondary"
						/>
					)
				},
				enableSorting: false,
			},
			{
				id: 'shippingRateName',
				accessorKey: 'shippingRateName',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.name')} />,
				cell: ({ row }) => {
					const shippingRate = row.original
					return (
						<div className="min-w-0">
							<div className="truncate font-medium">{shippingRate.shippingRateName}</div>
							{shippingRate.description && (
								<div className="truncate text-xs text-muted-foreground">{shippingRate.description}</div>
							)}
						</div>
					)
				},
				meta: {
					label: t('table.columns.name'),
					placeholder: t('table.searchPlaceholder'),
					variant: 'text',
					icon: () => <Truck />,
				},
				enableColumnFilter: false,
				enableSorting: true,
			},
			{
				id: 'actions',
				cell: ({ row }) => {
					const shippingRate = row.original
					return (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									aria-label={t('table.openMenu')}
									variant="ghost"
									className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
								>
									<Ellipsis className="h-4 w-4" aria-hidden="true" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[160px]">
								<DropdownMenuItem onClick={() => onEditShippingRate(shippingRate.id)}>
									{t('table.editAction')}
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => onDeleteShippingRate(shippingRate.id)}
									className="text-destructive focus:text-destructive"
								>
									{t('table.deleteAction')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)
				},
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
		],
		[t, onEditShippingRate, onDeleteShippingRate]
	)
}

